/**
 * TitleRiskBadge — case-header pill summarising the HMLR Register
 * Extract risk score. Renders as one of four colour states with a
 * tooltip listing every factor.
 *
 * Contract:
 *   score = {
 *     level: "clean" | "caution" | "warning" | "critical",
 *     summary: string,
 *     factors: [{ code, label, weight, detail }],
 *     caution_count, warning_count, critical_count: number
 *   }
 *
 * When level="clean" AND showClean=false (default) we render nothing —
 * the case header stays uncluttered. Pass showClean when you actually
 * want the "Clean title" chip (e.g. on the standalone register-view page).
 */
import { useState } from "react";
import {
  ShieldCheck,
  Warning as WarnIcon,
  WarningOctagon,
  Info,
} from "@phosphor-icons/react";

const LEVEL_STYLES = {
  clean: {
    ring: "ring-emerald-300",
    bg: "bg-emerald-50",
    text: "text-emerald-900",
    dot: "bg-emerald-500",
    Icon: ShieldCheck,
  },
  caution: {
    ring: "ring-amber-300",
    bg: "bg-amber-50",
    text: "text-amber-900",
    dot: "bg-amber-500",
    Icon: Info,
  },
  warning: {
    ring: "ring-orange-300",
    bg: "bg-orange-50",
    text: "text-orange-900",
    dot: "bg-orange-500",
    Icon: WarnIcon,
  },
  critical: {
    ring: "ring-rose-300",
    bg: "bg-rose-50",
    text: "text-rose-900",
    dot: "bg-rose-600",
    Icon: WarningOctagon,
  },
};

const WEIGHT_LABEL = {
  caution: "Routine",
  warning: "Non-standard",
  critical: "Blocker",
};

export function TitleRiskBadge({ score, showClean = false, testid = "title-risk-badge" }) {
  const [open, setOpen] = useState(false);
  if (!score) return null;
  if (score.level === "clean" && !showClean) return null;

  const style = LEVEL_STYLES[score.level] || LEVEL_STYLES.caution;
  const { Icon } = style;

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ring-1 ${style.ring} ${style.bg} ${style.text} text-[11px] font-semibold hover:ring-2 transition-shadow`}
        data-testid={`${testid}-toggle`}
      >
        <Icon weight="fill" className="w-3.5 h-3.5" />
        <span className="uppercase tracking-wide">{score.level}</span>
        <span className="font-normal text-slate-600 max-w-[240px] truncate">{score.summary}</span>
      </button>

      {open && score.factors?.length > 0 && (
        <div
          className="absolute left-0 top-full mt-1 z-30 w-[360px] rounded-md border border-slate-200 bg-white shadow-lg p-3 space-y-2"
          data-testid={`${testid}-tooltip`}
        >
          <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
            <span className={`w-2 h-2 rounded-full ${style.dot}`} />
            <span className="text-xs font-semibold text-slate-900">
              Title risk — {score.factors.length} factor{score.factors.length !== 1 ? "s" : ""}
            </span>
            <span className="ml-auto text-[10px] text-slate-500">
              {score.critical_count > 0 && <span className="text-rose-700 font-semibold mr-1">{score.critical_count} critical</span>}
              {score.warning_count > 0 && <span className="text-orange-700 font-semibold mr-1">{score.warning_count} warning</span>}
              {score.caution_count > 0 && <span className="text-amber-700 font-semibold">{score.caution_count} caution</span>}
            </span>
          </div>
          <ul className="space-y-2 max-h-72 overflow-y-auto">
            {score.factors.map((f) => {
              const fs = LEVEL_STYLES[f.weight === "critical" ? "critical" : f.weight === "warning" ? "warning" : "caution"];
              return (
                <li key={f.code} className="flex gap-2" data-testid={`${testid}-factor-${f.code}`}>
                  <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${fs.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[12px] font-semibold text-slate-900">{f.label}</span>
                      <span className={`text-[9.5px] uppercase tracking-wide ${fs.text}`}>
                        {WEIGHT_LABEL[f.weight] || f.weight}
                      </span>
                    </div>
                    {f.detail && <div className="text-[11px] text-slate-600 leading-snug">{f.detail}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="pt-1 border-t border-slate-100 text-[10px] text-slate-500">
            Derived from the HMLR Register Extract Service (Register Entry Indicators + class of title).
          </div>
        </div>
      )}
    </div>
  );
}

export default TitleRiskBadge;
